import { Component } from '@theme/component';
import { readCookie, saveOrbePreference, syncOrbePreference } from '@theme/orbe-localization';

/**
 * <geolocation-popup> — theme-native replacement for the Orbe geolocation popup.
 *
 * Orbe's own popup is suppressed by `sections/geolocation-popup.liquid`, but the app still runs its
 * geolocation lookup and still auto-redirects returning visitors who have a saved preference. This
 * element owns only the first-visit decision: it asks whether the visitor wants the market matching
 * where they are, and records the answer as an Orbe preference so the app stops asking.
 *
 * Country detection prefers Orbe's own lookup and falls back to Shopify's
 * `browsing_context_suggestions.json` — the same endpoint Orbe itself falls back to.
 *
 * @extends {Component}
 */
class GeolocationPopup extends Component {
  /** Cookie recording that the visitor closed the popup without switching. */
  static dismissedCookie = 'theme_geo_popup_dismissed';

  /** How long to wait for Orbe's lookup before falling back, in milliseconds. */
  static orbeTimeout = 2000;

  connectedCallback() {
    super.connectedCallback();

    this.#dialogComponent?.addEventListener('dialog:close', this.#onDialogClose);
    this.#select?.addEventListener('change', this.#onCountryChange);

    // In the theme editor, selecting the section previews it instead of waiting on geolocation.
    document.addEventListener('shopify:section:select', this.#onSectionSelect);
    document.addEventListener('shopify:section:deselect', this.#onSectionDeselect);

    const dialog = this.#dialog;

    if (dialog) this.#dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });

    if (this.#recoverStrandedDialog()) return;

    this.#maybeShow().catch((error) => console.error('[geolocation-popup]', error));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#dialogObserver.disconnect();
    this.#dialogComponent?.removeEventListener('dialog:close', this.#onDialogClose);
    this.#select?.removeEventListener('change', this.#onCountryChange);
    document.removeEventListener('shopify:section:select', this.#onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#onSectionDeselect);
  }

  /**
   * A re-render can strand the dialog at any point, not just before this element is connected — the
   * theme editor re-renders on every setting change, and morph patches the `open` attribute in
   * place without the element ever being replaced. Watching the attribute catches every route.
   */
  #dialogObserver = new MutationObserver(() => this.#recoverStrandedDialog());

  /**
   * @param {Event} event - The section select event.
   */
  #onSectionSelect = (event) => {
    if (!(event.target instanceof Element) || !event.target.contains(this)) return;

    this.#reopen().catch((error) => console.error('[geolocation-popup]', error));
  };

  /**
   * Re-renders — a theme-editor setting change, or any Section Rendering API update — replace this
   * element wholesale. The replacement `<dialog>` carries the `open` attribute across but is no
   * longer in the browser's top layer, so it renders inline in the page with no backdrop. Put it
   * back into a real modal instead of leaving it stranded in the layout.
   *
   * @returns {boolean} Whether a stranded dialog was taken over.
   */
  #recoverStrandedDialog() {
    const dialog = this.#dialog;

    if (!dialog || !dialog.open || this.#isTopLayer(dialog)) return false;

    // Clearing `open` first, so `showDialog` doesn't treat it as already shown. Both writes come
    // back through the observer, but by then the guard above holds.
    dialog.removeAttribute('open');
    this.#reopen().catch((error) => console.error('[geolocation-popup]', error));

    return true;
  }

  /**
   * Opens the dialog and fills in the detected country. Used by the theme editor and after a
   * re-render, where the geolocation gate in `#maybeShow` has already been passed or doesn't apply.
   */
  async #reopen() {
    this.#previewing = Boolean(window.Shopify?.designMode);

    const currentCountry = this.dataset.currentCountry ?? '';
    // Reuse what the popup was already showing — a re-render resets the copy to the server's empty
    // placeholder, but the country it was offering hasn't changed.
    const country = this.#country ?? (await this.#detectCountry());

    // The popup only has something to say about a country other than the current market, so fall
    // back to any other one rather than showing empty copy.
    this.#populate(country && country !== currentCountry ? country : this.#anyOtherCountry(currentCountry));
    this.#dialogComponent?.showDialog?.();
  }

  /**
   * `:modal` is only true for dialogs opened with `showModal()`, which is exactly the distinction
   * we need. Older engines throw on the selector rather than returning false.
   *
   * @param {HTMLDialogElement} dialog - The dialog to test.
   * @returns {boolean}
   */
  #isTopLayer(dialog) {
    try {
      return dialog.matches(':modal');
    } catch {
      // Can't tell — assume it's fine rather than risk closing a working modal.
      return true;
    }
  }

  /**
   * @param {Event} event - The section deselect event.
   */
  #onSectionDeselect = (event) => {
    if (!(event.target instanceof Element) || !event.target.contains(this)) return;

    this.#dialogComponent?.closeDialog?.();
  };

  /** Whether the dialog is open for a theme-editor preview rather than a real visit. */
  #previewing = false;

  /**
   * The country the popup is currently offering, kept so it can be restored after a re-render wipes
   * the copy back to the server's empty placeholder.
   *
   * @type {string | undefined}
   */
  #country;

  /**
   * Keeps the flag in the field in step with the dropdown. A native select can't render images in
   * its options, so the flag lives alongside it.
   */
  #onCountryChange = () => {
    const flag = this.querySelector('[data-geo-flag]');
    const source = this.#select?.selectedOptions[0]?.dataset.flag;

    if (flag instanceof HTMLImageElement && source) flag.src = source;
  };

  /** @returns {(Element & { showDialog?: () => void, closeDialog?: () => void }) | null} */
  get #dialogComponent() {
    return this.querySelector('dialog-component');
  }

  /**
   * Queried rather than taken from `refs`: `Component` scopes refs to the nearest `*-component`
   * ancestor, so the dialog belongs to `<dialog-component>`, not to this element.
   *
   * @returns {HTMLDialogElement | null}
   */
  get #dialog() {
    return this.querySelector('dialog.geo-popup__dialog');
  }

  /** @returns {HTMLFormElement | null} */
  get #form() {
    return this.querySelector('form.geo-popup__form');
  }

  /**
   * Appending `?geo_popup` to any URL forces the popup open, so it can be reviewed without
   * clearing cookies or browsing from another country.
   *
   * @returns {boolean}
   */
  get #forced() {
    return new URLSearchParams(window.location.search).has('geo_popup');
  }

  /**
   * Decides whether the visitor should be asked, and shows the popup if so.
   */
  async #maybeShow() {
    const currentCountry = this.dataset.currentCountry;

    if (!currentCountry || !this.#form) return;

    if (!this.#forced) {
      // The theme editor reloads constantly — don't fight the merchant.
      if (window.Shopify?.designMode) return;

      // Already answered, here or in Orbe's own selector. Returning visitors with a saved
      // preference are Orbe's to redirect.
      if (readCookie(GeolocationPopup.dismissedCookie)) return;
      if (readCookie('mdApp_countryCodeDomain')) return;
    }

    const detected = this.#forced ? this.#anyOtherCountry(currentCountry) : await this.#detectCountry();

    // Nothing to offer: detection failed, they're already in the right market, or we don't sell
    // into their country.
    if (!detected || detected === currentCountry) return;

    // We don't sell into their country, so there's nothing to offer.
    if (!this.#populate(detected)) return;

    this.#dialogComponent?.showDialog?.();
  }

  /**
   * Preselects a country and writes its name into the copy. The dropdown is still the real choice —
   * this is only the starting point.
   *
   * @param {string} [countryCode] - The detected country's ISO code.
   * @returns {boolean} Whether the country is one this storefront sells into.
   */
  #populate(countryCode) {
    const option = countryCode
      ? this.#select?.querySelector(`option[value="${CSS.escape(countryCode)}"]`)
      : null;

    if (!(option instanceof HTMLOptionElement)) return false;

    this.#country = option.value;
    if (this.#select) this.#select.value = option.value;
    this.#onCountryChange();

    for (const element of this.querySelectorAll('[data-geo-detected-name]')) {
      element.textContent = this.#countryName(option);
    }

    return true;
  }

  /** @returns {HTMLSelectElement | null} */
  get #select() {
    return this.querySelector('[data-geo-country-select]');
  }

  /**
   * Strips the trailing currency code off an option label, so the prose reads "you're in United
   * Kingdom" rather than "United Kingdom (GBP)".
   *
   * @param {HTMLOptionElement} option - The country option.
   * @returns {string}
   */
  #countryName(option) {
    return (option.textContent ?? '').replace(/\s*\([^)]*\)\s*$/, '').trim();
  }

  /**
   * Picks a country other than the current one, so `?geo_popup` has something to display.
   *
   * @param {string} currentCountry - The country the visitor is currently browsing in.
   * @returns {string | undefined}
   */
  #anyOtherCountry(currentCountry) {
    const options = [...(this.#select?.options ?? [])];

    return options.find((option) => option.value !== currentCountry)?.value;
  }

  /**
   * Works out which country the visitor is in.
   *
   * @returns {Promise<string | undefined>}
   */
  async #detectCountry() {
    return (await this.#detectViaOrbe()) ?? (await this.#detectViaShopify());
  }

  /**
   * Waits for Orbe's geolocation lookup, which is already in flight, to land.
   *
   * @returns {Promise<string | undefined>}
   */
  async #detectViaOrbe() {
    const deadline = Date.now() + GeolocationPopup.orbeTimeout;

    while (Date.now() < deadline) {
      const country = window.orbito?.remoteCountryCode;

      if (country) return country;

      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    return undefined;
  }

  /**
   * Falls back to Shopify's own detection, for when Orbe is blocked, over quota, or disabled.
   *
   * @returns {Promise<string | undefined>}
   */
  async #detectViaShopify() {
    const root = window.Shopify?.routes?.root ?? '/';

    try {
      const response = await fetch(`${root}browsing_context_suggestions.json?country[enabled]=true`);
      const data = await response.json();

      return data?.detected_values?.country?.handle;
    } catch (error) {
      console.error('[geolocation-popup] country detection failed', error);
      return undefined;
    }
  }

  /**
   * Switches to whichever country is selected in the dropdown. Shopify handles the redirect when
   * that market has its own domain; `syncOrbePreference` makes sure Orbe doesn't bounce the visitor
   * back.
   */
  accept() {
    const form = this.#form;
    const chosen = this.#select?.value;

    if (!form || !chosen) return;

    // Don't navigate the merchant away mid-edit.
    if (this.#previewing) {
      this.dismiss();
      return;
    }

    // Picking the market they're already in is just a way of saying "stay here" — no need to
    // round-trip through Shopify for it.
    if (chosen === this.dataset.currentCountry) {
      this.dismiss();
      return;
    }

    const input = form.querySelector('[data-geo-country-input]');

    if (input instanceof HTMLInputElement) input.value = chosen;

    syncOrbePreference(form, chosen);
    form.submit();
  }

  /**
   * Closes the popup. Recording the choice happens in the close handler so that dismissing via the
   * backdrop or Escape counts too.
   */
  dismiss() {
    this.#dialogComponent?.closeDialog?.();
  }

  /**
   * Records staying put as a deliberate choice: the dismissal cookie stops this popup reappearing,
   * and the Orbe preference stops the app from acting on the geolocation itself.
   */
  #onDialogClose = () => {
    // A theme-editor preview isn't a visitor decision — don't record anything.
    if (this.#previewing) {
      this.#previewing = false;
      return;
    }

    const currentCountry = this.dataset.currentCountry;
    const days = Number(window.mdApp_cookiesSettings?.modal_close_time) || 7;

    document.cookie = `${GeolocationPopup.dismissedCookie}=1;path=/;max-age=${days * 86400};`;

    if (currentCountry) saveOrbePreference(currentCountry);
  };
}

if (!customElements.get('geolocation-popup')) {
  customElements.define('geolocation-popup', GeolocationPopup);
}
